import { ReactElement } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  ButtonGroup,
  MenuList,
  MenuItem,
  PopoverRoot,
  PopoverTrigger,
  PopoverPopup,
  openModal,
  Box,
} from '@ff-it/ui';
import { DownloadButton } from 'components';
import { Icon, typeIconMap } from 'components/Icon';
import { CreateBlockDialog } from 'modules/campaign/block/dialogs';
import type { BlockDetails } from 'modules/campaign/block/types';
import { useAgencyTypeOptions } from 'modules/core/useEnabledTypeOptions';
import type { CampaignDetails } from '../types';

interface ToolbarProps {
  addBlock: (block: BlockDetails) => void;
  campaign: CampaignDetails;
}

export function Toolbar({ campaign, addBlock }: ToolbarProps): ReactElement {
  const activityTypeOptions = useAgencyTypeOptions();

  const openCreateBlock = (type: BlockDetails['type']) =>
    openModal<BlockDetails>((props) => <CreateBlockDialog {...props} type={type} campaign={campaign} />, {
      className: 'right',
      dismissProps: { outsidePress: false },
    }).then((res) => {
      if (res) {
        addBlock(res);
        toast.success('Block has been added.', { toastId: 'add-block-toast' });
      }
    });

  return (
    <Box display="flex" flexGrow={1} ml="2">
      <PopoverRoot>
        <PopoverTrigger>
          <Button size="sm" variant="primary" icon="plus" testId="add-block">
            Add block
          </Button>
        </PopoverTrigger>
        <PopoverPopup closeOnContentClick placement="bottom-end">
          <MenuList testId="toolbar-add-controls">
            {activityTypeOptions.map(({ label, value: type }) => (
              <MenuItem
                key={type}
                testId={`add-block-${type}`}
                label={label}
                prefix={<Icon icon={typeIconMap[type]} />}
                onClick={() => openCreateBlock(type)}
              />
            ))}
          </MenuList>
        </PopoverPopup>
      </PopoverRoot>

      <ButtonGroup ml="auto" data-testid="toolbar-export-controls">
        <DownloadButton
          url={`campaign/campaigns/${campaign.id}/export_blocks/`}
          icon="file-excel"
          variant="ghost"
          title="Export blocks"
        >
          Export blocks
        </DownloadButton>
        <DownloadButton
          url={`campaign/campaigns/${campaign.id}/export_rows/`}
          icon="file-excel"
          variant="ghost"
          title="Export rows"
        >
          Export rows
        </DownloadButton>
      </ButtonGroup>
    </Box>
  );
}
